import { normalizarFechaTareo } from './tareoEventoEfectivo';
import calculosBoletaInternaService from '../services/calculosBoletaInternaService';
import {
  calcularIngresosDiasEspeciales,
  prorratearSueldoPorVacaciones,
  calcularDiasEfectivosIngresoCese,
  esCodigoDiaEspecial,
} from './ingresosDiasEspeciales';
import {
  calcularDescuentosOperativosInterna,
  esCodigoDescuentoOperativo,
} from './descuentosOperativosInterna';
import { obtenerPeriodoLaboralEnRango } from './fechaImportacion';
import { conceptoTareoAplicaACanal } from './ajusteConceptoDestino';
import { esConceptoDeclaracionViatico } from './gratificacionPlame';
import {
  conceptoEstaEnEstaticos,
  debeConservarMontoEstaticoFicha,
  esRegimenAgrarioDeEmpleado,
} from './conceptosEstaticosPlanilla';

function codigoConceptoUsuario(c) {
  return String(
    c?.codigoPlame
    || c?.codigo_plame
    || c?.codigo
    || c?.plame_configuracion?.codigo_plame
    || c?.plameConfiguracion?.codigo_plame
    || ''
  );
}

/** Días distintos presentes en los tareos del tramo. */
export function contarDiasUnicosTareo(tareos) {
  const set = new Set();
  (tareos || []).forEach((t) => {
    const f = normalizarFechaTareo(t?.fecha);
    if (f) set.add(f);
  });
  return set.size;
}

/**
 * @returns {Promise<object|null>} resultado de calculosBoletaInternaService
 */
export async function calcularBoletaInternaUna({
  datosCompletos,
  resultadoBoleta,
  datosDisponibles,
  empleado,
  sueldoBaseInterna,
  tareos,
  omitirDescPlameInterna = false,
  incluirPlantilla = true,
  minutosDeficit = 0,
  promedioHorasTeoricasDiarias = 8,
  esSoloInterna = false,
  conceptosUsuario = {},
  etiqueta = 'planilla_calculo_interna',
  /** Si true: 0121 = (sueldo/díasMes) * días del tramo (− vacaciones del tramo) */
  prorratearPorDiasTramo = false,
}) {
  const feriadosCfg = datosDisponibles?.apis?.feriadosPagoDoble || [];
  const descansoLaboradoPagoDoble = Boolean(
    datosDisponibles?.apis?.configuracionDescansoLaborado?.pago_doble
  );
  const conceptosPlameInterna =
    datosDisponibles?.apis?.conceptosPlame || datosCompletos.datosAPIs?.conceptosPlame || [];
  const diasTramo = contarDiasUnicosTareo(tareos);

  const { ingresos: ingresosEspecialesInterna, diasEspeciales: diasEspInterna } =
    calcularIngresosDiasEspeciales({
      sueldoBase: sueldoBaseInterna,
      tareos: tareos || [],
      analisisPeriodo: datosDisponibles?.analisisPeriodo,
      fechasFeriadoPagoDoble: feriadosCfg,
      descansoLaboradoPagoDoble,
      conceptosPlame: conceptosPlameInterna,
    });

  const empDatos = empleado.datosEmpleado || {};
  const histInternoRegs = empDatos.historial_laboral_interno || empDatos.historialLaboralInterno || [];
  let historialParaInterna = {
    fechaIngresoActual: datosDisponibles?.historialLaboral?.fechaIngresoActual || null,
    fechaCeseActual: datosDisponibles?.historialLaboral?.fechaCeseActual || null,
  };
  if (histInternoRegs.length > 0 && datosDisponibles?.analisisPeriodo) {
    const planillaIni = datosDisponibles.analisisPeriodo.fechaInicio;
    const planillaFin = datosDisponibles.analisisPeriodo.fechaFin;
    const periodoInt = obtenerPeriodoLaboralEnRango(
      { historial_laboral: histInternoRegs },
      planillaIni,
      planillaFin
    );
    const ordenadoInt = [...histInternoRegs].sort(
      (a, b) => new Date(b.fecha_ingreso) - new Date(a.fecha_ingreso)
    );
    historialParaInterna = {
      fechaIngresoActual: periodoInt?.fecha_ingreso || ordenadoInt[0]?.fecha_ingreso || null,
      fechaCeseActual: periodoInt?.fecha_cese || ordenadoInt[0]?.fecha_cese || null,
    };
  } else if (empDatos.fecha_ingreso_interna || datosCompletos.datosSalariales?.fechaIngresoInterna) {
    historialParaInterna = {
      fechaIngresoActual:
        empDatos.fecha_ingreso_interna
        || datosCompletos.datosSalariales?.fechaIngresoInterna
        || historialParaInterna.fechaIngresoActual,
      fechaCeseActual: historialParaInterna.fechaCeseActual,
    };
  }

  const diasMaximosInternaCalculados = calcularDiasEfectivosIngresoCese(
    datosDisponibles?.analisisPeriodo,
    historialParaInterna
  );

  // En tramos: el tope de días del 0121 es solo los días asignados a esta parte
  const diasBasePara0121 = prorratearPorDiasTramo
    ? diasTramo
    : diasMaximosInternaCalculados;

  let sueldoInternoProrrateado = 0;
  if (prorratearPorDiasTramo && diasTramo <= 0) {
    sueldoInternoProrrateado = 0;
  } else {
    sueldoInternoProrrateado = prorratearSueldoPorVacaciones(
      sueldoBaseInterna,
      diasEspInterna?.vacaciones || 0,
      datosDisponibles?.analisisPeriodo,
      diasBasePara0121
    );
  }

  let diasBaseMesInterna = 30;
  if (datosDisponibles?.analisisPeriodo?.tipo === 'mes_unico') {
    diasBaseMesInterna = Number(datosDisponibles.analisisPeriodo.mes?.diasCompletosDelMes) || 30;
  }
  const diasMaximosInterna = prorratearPorDiasTramo
    ? diasTramo
    : Math.min(
      30,
      diasMaximosInternaCalculados != null ? diasMaximosInternaCalculados : diasBaseMesInterna
    );
  const vacInterna = diasEspInterna?.vacaciones || 0;
  const diasEfectivosInternaFactor = Math.max(0, Math.min(30, diasMaximosInterna - vacInterna));
  const factorProporcionalInterna =
    diasBaseMesInterna > 0 ? diasEfectivosInternaFactor / diasBaseMesInterna : 1;

  const { descuentos: descuentosOperativosInterna } =
    calcularDescuentosOperativosInterna({
      sueldoBase: sueldoBaseInterna,
      tareos: tareos || [],
      analisisPeriodo: datosDisponibles?.analisisPeriodo,
      descansosSemanales: datosDisponibles?.descansosSemanales || null,
      minutosDeficit: typeof minutosDeficit === 'number' ? minutosDeficit : 0,
      promedioHorasTeoricasDiarias:
        typeof promedioHorasTeoricasDiarias === 'number' ? promedioHorasTeoricasDiarias : 8,
      conceptosPlame: conceptosPlameInterna,
    });

  const aplicarFactorIngresoInterno = (c) => {
    const listaEstaticos =
      datosDisponibles?.conceptosEstaticos?.completos
      || datosDisponibles?.apis?.conceptosEstaticos
      || datosCompletos?.datosAPIs?.conceptosEstaticos
      || [];
    const esAgrarioInterna = esRegimenAgrarioDeEmpleado({
      codigo: datosDisponibles?.regimenLaboral,
      nombre: datosCompletos?.datosSalariales?.regimenLaboral,
      regimenSeleccionado: datosCompletos?.datosAPIs?.regimenSeleccionado,
    });
    if (
      debeConservarMontoEstaticoFicha(
        conceptoEstaEnEstaticos(listaEstaticos, c),
        esAgrarioInterna,
        c.codigo_plame ?? c.codigoPlame ?? c.codigo ?? c.plame_configuracion?.codigo_plame
      )
    ) {
      return c;
    }
    if (!(factorProporcionalInterna < 0.999)) return c;
    const monto = Number(c.monto) || 0;
    if (monto <= 0) return c;
    const origen = String(c.origenCalculo || c.origen_calculo || c.origen || '').toLowerCase();
    const esDesdeTareo = Boolean(
      c.origenTareo
      || c.origen_tareo
      || c.desdeTareo
      || origen === 'tareo'
      || origen === 'viatico'
      || origen.includes('viatico')
    );
    if (esDesdeTareo) {
      const montoTareo = Number(c.montoTareo);
      if (Number.isFinite(montoTareo) && montoTareo > 0 && montoTareo < monto - 0.001) {
        const partePlantilla = monto - montoTareo;
        const plantillaProrr = parseFloat((partePlantilla * factorProporcionalInterna).toFixed(2));
        return { ...c, monto: parseFloat((plantillaProrr + montoTareo).toFixed(2)) };
      }
      return c;
    }
    return { ...c, monto: parseFloat((monto * factorProporcionalInterna).toFixed(2)) };
  };

  // Conceptos plantilla (sin tareo) + conceptos de tareo del tramo ya vienen en datosCompletos;
  // aquí filtramos plantilla si corresponde y recalculamos especiales/operativos.
  const ingresosPlantilla = incluirPlantilla
    ? (datosCompletos.conceptosUsuario.ingresosInternos || [])
        .filter((c) => {
          const cod = codigoConceptoUsuario(c);
          if (!cod || cod === '0121' || esCodigoDiaEspecial(cod)) return false;
          if (esConceptoDeclaracionViatico(c) || codigoConceptoUsuario(c) === '0917') return false;
          const origen = String(c.origenCalculo || c.origen_calculo || c.origen || '').toLowerCase();
          const esTareo = Boolean(
            c.origenTareo || c.origen_tareo || origen === 'tareo' || origen.includes('viatico')
          );
          return !esTareo;
        })
        .map((c) => {
          const cod = codigoConceptoUsuario(c);
          return aplicarFactorIngresoInterno({
            ...c,
            codigo: cod,
            codigo_plame: cod,
            codigoPlame: cod,
          });
        })
    : [];

  // Conceptos de tareo interna de ESTE tramo
  const ingresosTareoFiltrados = [];
  (tareos || []).forEach((tareo) => {
    (tareo.conceptos || []).forEach((c) => {
      if (!conceptoTareoAplicaACanal(c, 'interna', tareo.conceptos, empleado)) return;
      if (String(c.tipo_concepto || '') !== 'ingreso') return;
      const codigo =
        c.plame_configuracion?.codigo_plame || c.codigo_plame || c.codigo || '';
      const monto = parseFloat(c.monto) || 0;
      if (!codigo || monto <= 0) return;
      const existente = ingresosTareoFiltrados.find(
        (x) => codigoConceptoUsuario(x) === String(codigo)
      );
      if (existente) {
        existente.monto = (Number(existente.monto) || 0) + monto;
        existente.montoTareo = (Number(existente.montoTareo) || 0) + monto;
        return;
      }
      ingresosTareoFiltrados.push({
        codigo,
        codigo_plame: codigo,
        codigoPlame: codigo,
        monto,
        montoTareo: monto,
        tipo_concepto: 'ingreso',
        descripcion: c.plame_configuracion?.descripcion || c.descripcion || codigo,
        origenTareo: true,
        origen_tareo: true,
        origenCalculo: 'tareo',
      });
    });
  });

  const ingresosInternosBase = [...ingresosPlantilla, ...ingresosTareoFiltrados];

  ingresosEspecialesInterna.forEach((ing) => {
    const cod = String(ing.codigoPlame || '');
    const existente = ingresosInternosBase.find((x) => codigoConceptoUsuario(x) === cod);
    if (existente) {
      existente.monto = Number(ing.monto) || 0;
      existente.concepto = ing.concepto || existente.concepto;
      existente.descripcion = ing.descripcion || existente.descripcion;
      return;
    }
    ingresosInternosBase.push(ing);
  });

  const CODIGOS_DESC_OMITIR_INTERNA = new Set([
    '0601', '0606', '0608', '0607', '0611', '0616',
  ]);

  const descuentosPlantilla = incluirPlantilla
    ? (datosCompletos.conceptosUsuario.descuentosInternos || [])
        .filter((c) => {
          const cod = codigoConceptoUsuario(c);
          if (!cod || esCodigoDescuentoOperativo(cod)) return false;
          if (omitirDescPlameInterna && CODIGOS_DESC_OMITIR_INTERNA.has(cod)) return false;
          const origen = String(c.origenCalculo || c.origen_calculo || c.origen || '').toLowerCase();
          const esTareo = Boolean(c.origenTareo || c.origen_tareo || origen === 'tareo');
          return !esTareo;
        })
        .map((c) => {
          const cod = codigoConceptoUsuario(c);
          return { ...c, codigo: cod, codigo_plame: cod, codigoPlame: cod };
        })
    : [];

  const descuentosTareoFiltrados = [];
  (tareos || []).forEach((tareo) => {
    (tareo.conceptos || []).forEach((c) => {
      if (!conceptoTareoAplicaACanal(c, 'interna', tareo.conceptos, empleado)) return;
      if (!['descuento', 'adelanto'].includes(String(c.tipo_concepto || ''))) return;
      const codigo =
        c.plame_configuracion?.codigo_plame || c.codigo_plame || c.codigo || '';
      const monto = parseFloat(c.monto) || 0;
      if (!codigo || monto <= 0) return;
      if (omitirDescPlameInterna && CODIGOS_DESC_OMITIR_INTERNA.has(String(codigo))) return;
      const existente = descuentosTareoFiltrados.find(
        (x) => codigoConceptoUsuario(x) === String(codigo)
      );
      if (existente) {
        existente.monto = (Number(existente.monto) || 0) + monto;
        return;
      }
      descuentosTareoFiltrados.push({
        codigo,
        codigo_plame: codigo,
        codigoPlame: codigo,
        monto,
        tipo_concepto: 'descuento',
        descripcion: c.plame_configuracion?.descripcion || c.descripcion || codigo,
        origenTareo: true,
        origen_tareo: true,
        origenCalculo: 'tareo',
      });
    });
  });

  const descuentosInternosBase = [...descuentosPlantilla, ...descuentosTareoFiltrados];

  descuentosOperativosInterna.forEach((desc) => {
    const cod = String(desc.codigoPlame || '');
    const existente = descuentosInternosBase.find((x) => codigoConceptoUsuario(x) === cod);
    if (existente) {
      existente.monto = Number(desc.monto) || 0;
      existente.concepto = desc.concepto || existente.concepto;
      existente.descripcion = desc.descripcion || existente.descripcion;
      return;
    }
    descuentosInternosBase.push(desc);
  });

  const datosInterna = {
    ...datosCompletos,
    omitir_descuentos_plame_interna: omitirDescPlameInterna,
    datosSalariales: {
      ...datosCompletos.datosSalariales,
      sueldoInterno: sueldoInternoProrrateado,
      sueldoInternoCompleto: sueldoBaseInterna,
      omitirDescuentosPlameInterna: omitirDescPlameInterna,
      tipoContratacion: esSoloInterna ? 'Interno' : datosCompletos.datosSalariales.tipoContratacion,
    },
    conceptosUsuario: {
      ...datosCompletos.conceptosUsuario,
      ingresos: esSoloInterna && incluirPlantilla
        ? (conceptosUsuario.ingresos || [])
            .filter((c) => {
              const cod = codigoConceptoUsuario(c);
              return cod && cod !== '0121' && !esCodigoDiaEspecial(cod);
            })
            .map((c) => {
              const cod = codigoConceptoUsuario(c);
              return aplicarFactorIngresoInterno({
                ...c,
                codigo: cod,
                codigo_plame: cod,
                codigoPlame: cod,
              });
            })
        : [],
      ingresosInternos: ingresosInternosBase,
      descuentosInternos: descuentosInternosBase,
    },
  };

  const resultado = await calculosBoletaInternaService.generarCalculos(
    datosInterna,
    resultadoBoleta,
    etiqueta
  );

  if (resultado && typeof resultado === 'object') {
    resultado.dias_tramo = diasTramo;
    resultado.sueldo_base_tramo = sueldoBaseInterna;
    resultado.sueldo_0121_prorrateado = sueldoInternoProrrateado;
    if (resultado.data && typeof resultado.data === 'object') {
      resultado.data.dias_tramo = diasTramo;
    }
  }

  return resultado;
}

export default { calcularBoletaInternaUna, contarDiasUnicosTareo };
